/**
 * OTA rollback guard — confirm the running bundle only once it has actually
 * rendered.
 *
 * The plugin reverts a freshly-applied bundle that fails to call
 * `notifyAppReady()` within `appReadyTimeout` (10s, capacitor.config.ts). That
 * is the whole footgun guard behind OTA: per
 * docs/platform/mobile-offline-cold-start.md a white-screen bundle self-heals and
 * cannot permanently brick a device.
 *
 * The guard is only worth anything if "ready" means *the app is on screen*.
 * Confirming at module-eval time (as the entry chunk used to) marks the bundle
 * good microseconds into startup, so anything that white-screens after that —
 * a render crash outside an error boundary, a lazy chunk missing from the zip, a
 * mount that never completes — is recorded as known-good and never rolled back.
 * `root.render()` returning proves nothing either: React 18 commits
 * asynchronously.
 *
 * So we watch the root container and confirm the first time it has content.
 *
 * **Why a MutationObserver and not `requestAnimationFrame`.** A staged bundle is
 * committed by `installNext()` when the app BACKGROUNDS, reloading the WebView
 * off-screen (see the design doc, "Apply on next background"). Chromium freezes
 * rAF while the document is hidden, so an rAF-driven poll would not run on that
 * reload — and a perfectly healthy bundle could miss `appReadyTimeout` and be
 * rolled back. MutationObserver callbacks are microtasks and fire regardless of
 * visibility.
 *
 * There is deliberately no give-up timer: if the tree never mounts we simply
 * never confirm, and the plugin's own `appReadyTimeout` does the rolling back.
 * A local deadline could only ever cause a *false* rollback of a slow-but-good
 * bundle — the failure mode we least want.
 */
import { notifyReady } from './liveUpdate';

/**
 * Watch `container` and call `notifyReady()` as soon as it has content; stay
 * silent forever if it never does. No-op off-native (`notifyReady` itself gates
 * on `isOtaEnabled`).
 */
export function confirmBundleAfterRender(container: HTMLElement): void {
  if (container.childElementCount > 0) {
    // React committed synchronously — nothing to wait for.
    void notifyReady();
    return;
  }

  const observer = new MutationObserver(() => {
    if (container.childElementCount === 0) return;
    observer.disconnect();
    void notifyReady();
  });
  observer.observe(container, { childList: true });
}
